import { useQuery, useMutation, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { billApi, landedCostCategoryApi } from '../services/friendlyApi';
import type {
  BillCreate,
  BillLineCategoryAssignment,
  BillLineCreate,
  BillShipmentLink,
  BillUpdate,
  ListBillsParams,
} from '../types';
import { ACCOUNTS_QUERY_KEY } from './useAccounts';
import { estimatesKeys } from '../../../shared/shipments/hooks/useEstimatesApi';
import { getBillFilterFacetsApiV1AccountingBillsFilterFacetsGet } from '../../../generated/api/accounting/accounting';

export const BILLS_QUERY_KEY = 'bills';
export const LANDED_COST_CATEGORIES_QUERY_KEY = 'landed-cost-categories';
export const UNBILLED_RECEIPTS_QUERY_KEY = 'unbilled-receipts';

export function useBills(params?: ListBillsParams, options?: { enabled?: boolean }) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, params],
    queryFn: () => billApi.list(params),
    placeholderData: keepPreviousData,
    // Opt-in gate: callers that only need bills under a condition (e.g. a dialog
    // that's mounted but closed) pass enabled:false to skip the fetch.
    enabled: options?.enabled ?? true,
  });
}

/** Vendor candidates from the bills list's base company + sharing scope. */
export function useBillFilterFacets(activeCompanyId?: string, adminScope?: string) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, 'filter-facets', activeCompanyId ?? null, adminScope ?? null],
    queryFn: () => getBillFilterFacetsApiV1AccountingBillsFilterFacetsGet(
      adminScope ? { admin_scope: adminScope } : undefined,
    ),
    staleTime: 5 * 60 * 1000,
  });
}

export function useBill(id: string) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, id],
    queryFn: () => billApi.get(id),
    enabled: !!id,
  });
}

/**
 * F-07: the PO / receipts / bill comparison for one bill, so the AP clerk sees
 * the variance BEFORE approving instead of discovering it in the 409 hold.
 * Keyed under the bill so approving (or relinking a PO) refreshes it.
 */
export function useBillThreeWayMatch(id: string, enabled = true) {
  return useQuery({
    queryKey: [BILLS_QUERY_KEY, id, 'three-way-match'],
    queryFn: () => billApi.threeWayMatch(id),
    enabled: enabled && !!id,
  });
}

/**
 * The estimate→actual surfaces a bill mutation moves (landed-cost estimates E3).
 *
 * Realization happens in a POST-COMMIT event subscriber, so the response the
 * mutation returns already predates it — without this the AP clerk approves the
 * freight bill and both the bill page's "vs estimate" strip and the shipment's
 * estimate tab keep showing the pre-match state until something else refetches.
 * Invalidating the whole `shipment-estimates` root covers the reconciliation and
 * bill-variance queries alike, and these mutations are rare human actions rather
 * than a hot path.
 */
function invalidateEstimateRealization(queryClient: ReturnType<typeof useQueryClient>) {
  queryClient.invalidateQueries({ queryKey: estimatesKeys.all });
}

export function useCreateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: BillCreate) => billApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: build a draft bill from a single confirmed goods receipt (GR CTA). */
export function useCreateBillFromReceipt() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goodsReceiptId: string) => billApi.fromReceipt(goodsReceiptId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: assemble one draft bill from several confirmed, unbilled receipts. */
export function useCreateBillFromReceipts() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (goodsReceiptIds: string[]) => billApi.fromReceipts(goodsReceiptIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** P2P-1: unbilled confirmed receipts for a vendor (picker source). */
export function useUnbilledReceipts(vendorId: string, companyId?: string, enabled = true) {
  return useQuery({
    queryKey: [UNBILLED_RECEIPTS_QUERY_KEY, vendorId, companyId ?? null],
    queryFn: () => billApi.unbilledReceipts(vendorId, companyId),
    enabled: enabled && !!vendorId,
  });
}

export function useUpdateBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillUpdate }) =>
      billApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useSubmitBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.submit(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useApproveBill() {
  const queryClient = useQueryClient();

  return useMutation({
    // acceptVariance: the explicit reviewer decision to approve despite an
    // out-of-tolerance three-way-match variance (BILL_THREE_WAY_MATCH_HOLD).
    mutationFn: ({ id, acceptVariance }: { id: string; acceptVariance?: boolean }) =>
      billApi.approve(id, acceptVariance),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
      invalidateEstimateRealization(queryClient);
    },
  });
}

/**
 * Bulk submit / approve (J8-01) — the AP clerk's batch path.
 *
 * Both resolve to `{ succeeded, skipped }`, so callers report partial results
 * rather than a bare success. Approve invalidates the accounts cache too: each
 * approval posts a journal entry.
 */
export function useBulkSubmitBills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (billIds: string[]) => billApi.bulkSubmit(billIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useBulkApproveBills() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (billIds: string[]) => billApi.bulkApprove(billIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

export function useDeleteBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

export function useVoidBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.void(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
      invalidateEstimateRealization(queryClient);
    },
  });
}

export function useCloneBill() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => billApi.clone(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
    },
  });
}

/**
 * Raise a draft vendor credit against a bill — the AP mirror of
 * `useCreateCreditNote`. Invalidates the accounts cache too: the credit is a
 * negative payable, so the AP control balance moves the moment it is approved.
 */
export function useCreateVendorCredit() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, lines, reason }: { id: string; lines?: BillLineCreate[]; reason?: string }) =>
      billApi.createVendorCredit(id, { lines, reason }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [ACCOUNTS_QUERY_KEY] });
    },
  });
}

/** T20B: link/unlink a bill to an inbound freight shipment + landed-cost category. */
export function useLinkBillShipment() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: BillShipmentLink }) =>
      billApi.linkShipment(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      invalidateEstimateRealization(queryClient);
    },
  });
}

/**
 * GAP-1: set the per-line landed-cost category on a mixed-charge forwarder
 * invoice, so the rollup splits it across categories instead of forcing the
 * whole bill onto the header category.
 */
export function useSetBillLineLandedCostCategories() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      assignments,
    }: {
      id: string;
      assignments: BillLineCategoryAssignment[];
    }) => billApi.setLineLandedCostCategories(id, assignments),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      invalidateEstimateRealization(queryClient);
    },
  });
}

/**
 * Invoice-first AP: manually link a DRAFT bill to a PO / goods receipt(s) so it
 * relieves GRNI (rebuilds the draft's lines from the selected receipts).
 */
export function useLinkBillPurchaseOrder() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, goodsReceiptIds }: { id: string; goodsReceiptIds: string[] }) =>
      billApi.linkPurchaseOrder(id, goodsReceiptIds),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: [BILLS_QUERY_KEY] });
      queryClient.invalidateQueries({ queryKey: [UNBILLED_RECEIPTS_QUERY_KEY] });
    },
  });
}

/** T20B: list the tenant's landed-cost categories for the bill link picker. */
export function useLandedCostCategories(isActive = true) {
  return useQuery({
    queryKey: [LANDED_COST_CATEGORIES_QUERY_KEY, { is_active: isActive }],
    queryFn: () => landedCostCategoryApi.list({ is_active: isActive }),
    staleTime: 5 * 60 * 1000,
  });
}
